// src/pages/Suscripcion.jsx
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { Browser } from '@capacitor/browser'
import { Purchases } from '@revenuecat/purchases-capacitor'
import { supabase } from '../lib/supabaseClient'
import { useLanguage } from '../hooks/useLanguage'
import { useAuth } from '../context/AuthContext' // ajusta la ruta si es distinta

const WOMPI_WIDGET_SRC = 'https://checkout.wompi.co/widget.js'
const ANDROID_PACKAGE_NAME = 'com.bonzzaienterprise.miplata'

const PLANES = {
  mensual: { montoUSD: 3.6, label_key: 'suscripcion_plan_mensual' },
  anual: { montoUSD: 50, label_key: 'suscripcion_plan_anual' },
}

// RevenueCat identifica los paquetes estándar así cuando la Offering
// usa los identificadores $rc_monthly / $rc_annual en el dashboard.
const RC_PACKAGE_ID = {
  mensual: '$rc_monthly',
  anual: '$rc_annual',
}

function loadWompiScript() {
  return new Promise((resolve, reject) => {
    if (window.WidgetCheckout) return resolve()
    const existing = document.querySelector(`script[src="${WOMPI_WIDGET_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', reject)
      return
    }
    const script = document.createElement('script')
    script.src = WOMPI_WIDGET_SRC
    script.onload = () => resolve()
    script.onerror = reject
    document.body.appendChild(script)
  })
}

export default function Suscripcion() {
  const { t, language } = useLanguage()
  const navigate = useNavigate()
  const { ready: authReady, refrescarAcceso, isPremium, trialActive, diasRestantes, subscriptionExpiresAt } = useAuth()

  const [planSeleccionado, setPlanSeleccionado] = useState('mensual')
  const [loadingPago, setLoadingPago] = useState(false)
  const [scriptListo, setScriptListo] = useState(false)
  const [estado, setEstado] = useState(null)
  const [errorMsg, setErrorMsg] = useState('')
  const [cotizacion, setCotizacion] = useState(null) // { montoCOP, trmUsada } — solo Wompi
  const [ofertaRC, setOfertaRC] = useState(null) // paquetes de RevenueCat en Android
   const [productoActivoId, setProductoActivoId] = useState(null)

  const esAppNativa = Capacitor.isNativePlatform()

  const LOCALE_POR_IDIOMA = { es: 'es-CO', en: 'en-US', fr: 'fr-FR' }
  const locale = LOCALE_POR_IDIOMA[language] || 'es-CO'

  // ── Web: cargar widget de Wompi ──
  useEffect(() => {
    if (esAppNativa) return
    if (isPremium) return // ya está suscrito, no hace falta cargar el widget
    loadWompiScript()
      .then(() => setScriptListo(true))
      .catch(() => setErrorMsg(t('suscripcion_error_widget')))
    // eslint-disable-next-line
  }, [isPremium])

  // ── Android: cargar oferta de RevenueCat ──
  useEffect(() => {
    if (!esAppNativa) return
    if (isPremium) return // ya está suscrito, no hace falta cargar la oferta
    // Esperamos a que AuthContext confirme que Purchases.logIn(userId) ya
    // terminó. Si comprabas antes de que esto completara, la compra quedaba
    // atada a un $RCAnonymousID en vez de al UUID real del usuario.
    if (!authReady) return
    Purchases.getOfferings()
      .then(({ current }) => setOfertaRC(current))
      .catch((err) => {
        console.error('Error cargando oferta RevenueCat:', err)
        setErrorMsg(t('suscripcion_error_generico'))
      })
    // eslint-disable-next-line
  }, [isPremium, authReady])

    // ── Android: cargar oferta de RevenueCat ──
  useEffect(() => {
    if (!esAppNativa) return
    if (isPremium) return
    if (!authReady) return
    Purchases.getOfferings()
      .then(({ current }) => setOfertaRC(current))
      .catch((err) => {
        console.error('Error cargando oferta RevenueCat:', err)
        setErrorMsg(t('suscripcion_error_generico'))
      })
    // eslint-disable-next-line
  }, [isPremium, authReady])

  const montoUSDFormateado = (usd) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(usd)

  const montoCOPFormateado = (cop) =>
    new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(cop)

  // ── Pago vía Google Play Billing (RevenueCat) ──
  async function handlePagarNativo() {
    setErrorMsg('')
    setEstado(null)

    const paqueteId = RC_PACKAGE_ID[planSeleccionado]
    const paquete = ofertaRC?.availablePackages?.find(p => p.identifier === paqueteId)

    if (!paquete) {
      setErrorMsg(t('suscripcion_error_generico'))
      return
    }

    setLoadingPago(true)
    try {
      const { customerInfo } = await Purchases.purchasePackage({ aPackage: paquete })
      const activo = !!customerInfo?.entitlements?.active?.['premium']

      setEstado(activo ? 'APPROVED' : 'PENDING')

      // El webhook de RevenueCat actualiza profiles en Supabase, pero puede
      // tardar unos segundos — refrescamos el acceso local ahora mismo (usando
      // el resultado que ya nos dio el SDK) y otra vez en unos segundos para
      // quedar sincronizados con el backend.
      await refrescarAcceso()
      setTimeout(() => refrescarAcceso(), 4000)
    } catch (err) {
      if (err?.userCancelled) {
        // el usuario cerró el flujo de pago de Google — no es un error real
      } else {
        console.error('Error al comprar con RevenueCat:', err)
        setErrorMsg(err.message || t('suscripcion_error_generico'))
      }
    } finally {
      setLoadingPago(false)
    }
  }

  // ── Pago vía Wompi (web) ──
  async function handlePagarWompi() {
    setErrorMsg('')
    setEstado(null)
    setLoadingPago(true)

    try {
      if (!scriptListo) await loadWompiScript()

      const { data, error } = await supabase.functions.invoke('wompi-checkout', {
        body: { plan: planSeleccionado },
      })

      if (error || !data) {
        throw new Error(error?.message || t('suscripcion_error_generico'))
      }

      const { publicKey, reference, amountInCents, currency, signature, montoCOP, trmUsada } = data
      setCotizacion({ montoCOP, trmUsada })

      const checkout = new window.WidgetCheckout({
        currency,
        amountInCents,
        reference,
        publicKey,
        signature: { integrity: signature },
      })

      checkout.open((result) => {
        setLoadingPago(false)
        const status = result?.transaction?.status
        setEstado(status || 'ERROR')
      })
    } catch (err) {
      console.error('Error al iniciar pago Wompi:', err)
      setErrorMsg(err.message || t('suscripcion_error_generico'))
      setLoadingPago(false)
    }
  }

  const handlePagar = esAppNativa ? handlePagarNativo : handlePagarWompi

  // ── Gestionar suscripción (cancelar/cambiar plan) ──
async function handleGestionarSuscripcion() {
  if (esAppNativa) {
    const url = productoActivoId
      ? `https://play.google.com/store/account/subscriptions?package=${ANDROID_PACKAGE_NAME}&sku=${productoActivoId}`
      : `https://play.google.com/store/account/subscriptions?package=${ANDROID_PACKAGE_NAME}`
    await Browser.open({ url })
  } else {
    window.location.href = 'mailto:soporte@bonzzaienterprise.com?subject=Gestionar%20mi%20suscripción'
  }
}

  const beneficios = [
    t('suscripcion_beneficio_1'),
    t('suscripcion_beneficio_2'),
    t('suscripcion_beneficio_3'),
    t('suscripcion_beneficio_4'),
  ]

  const planActual = PLANES[planSeleccionado]
  const ahorroAnual = PLANES.mensual.montoUSD * 12 - PLANES.anual.montoUSD

  // Precio a mostrar: en Android usamos el precio real que Google formatea
  // para el país del usuario (currentPrice.formatted); en web usamos USD.
  const paqueteActualRC = esAppNativa
    ? ofertaRC?.availablePackages?.find(p => p.identifier === RC_PACKAGE_ID[planSeleccionado])
    : null
  const precioMostrado = paqueteActualRC
    ? paqueteActualRC.product.priceString
    : montoUSDFormateado(planActual.montoUSD)

  const fechaVencimientoFormateada = subscriptionExpiresAt
    ? new Date(subscriptionExpiresAt).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' })
    : null

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>

      {/* ── HEADER ── */}
      <header style={{
        background: 'var(--color-surface)',
        borderBottom: '1px solid var(--color-border)',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}>
        <button
          onClick={() => navigate('/Perfil')}
          style={{ width: 36, height: 36, background: 'rgba(120,120,120,0.08)', border: '1px solid var(--color-border)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 18, color: 'var(--color-text)', transition: '0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(-2px)'; e.currentTarget.style.borderColor = 'var(--color-primary)' }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = 'var(--color-border)' }}
        >←</button>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{t('suscripcion_header')}</div>
      </header>

      {/* ── CONTENIDO ── */}
      <main style={{ maxWidth: 480, margin: '0 auto', padding: '28px 16px 60px' }}>

        {isPremium ? (
          /* ── YA ESTÁ SUSCRITO ── */
          <div style={{
            background: 'linear-gradient(135deg,#16a34a,#22c55e)',
            borderRadius: 24,
            padding: 28,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            border: '1px solid rgba(34,197,94,0.4)',
            boxShadow: 'var(--shadow-card)',
            marginBottom: 20,
            textAlign: 'center',
          }}>
            <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
            <div style={{ fontSize: 40, marginBottom: 10 }}>✅</div>
            <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 8 }}>
              {t('suscripcion_ya_suscrito_titulo') || 'Ya estás suscrito'}
            </div>
            <div style={{ fontSize: 14, opacity: 0.92, lineHeight: 1.5 }}>
              {fechaVencimientoFormateada
                ? `${t('suscripcion_vence_pre') || 'Tu suscripción se renueva el'} ${fechaVencimientoFormateada}`
                : (t('suscripcion_activa_sin_fecha') || 'Tu suscripción está activa.')}
            </div>

            <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {beneficios.map((b, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, justifyContent: 'center' }}>
                  <span>✅</span><span>{b}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* Selector mensual / anual */}
            <div style={{
              display: 'flex',
              gap: 8,
              marginBottom: 20,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 14,
              padding: 4,
            }}>
              {Object.entries(PLANES).map(([key, plan]) => (
                <button
                  key={key}
                  onClick={() => {
                    setPlanSeleccionado(key)
                    setCotizacion(null)
                  }}
                  style={{
                    flex: 1,
                    padding: '10px 8px',
                    borderRadius: 10,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 700,
                    background: planSeleccionado === key ? 'var(--color-primary)' : 'transparent',
                    color: planSeleccionado === key ? 'white' : 'var(--color-text)',
                    transition: 'var(--transition)',
                    position: 'relative',
                  }}
                >
                  {t(plan.label_key)}
                  {key === 'anual' && ahorroAnual > 0 && (
                    <span style={{
                      marginLeft: 6,
                      fontSize: 10,
                      fontWeight: 800,
                      background: planSeleccionado === key ? 'rgba(255,255,255,0.25)' : 'var(--stat-saving-1)',
                      color: 'white',
                      borderRadius: 6,
                      padding: '2px 6px',
                    }}>
                      -{Math.round((ahorroAnual / (PLANES.mensual.montoUSD * 12)) * 100)}%
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Card del plan */}
            <div style={{
              background: 'linear-gradient(135deg,var(--stat-saving-1),var(--stat-saving-2))',
              borderRadius: 24,
              padding: 26,
              color: 'white',
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid color-mix(in srgb, var(--color-primary) 40%, transparent)',
              boxShadow: 'var(--shadow-card)',
              marginBottom: 24,
            }}>
              <div style={{ position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />

              <div style={{ fontSize: 13, opacity: 0.8, marginBottom: 6 }}>{t('suscripcion_plan_nombre')}</div>
              <div style={{ fontSize: 36, fontWeight: 800, marginBottom: 2 }}>
                {precioMostrado}
              </div>
              <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 4 }}>
                {planSeleccionado === 'mensual' ? t('suscripcion_periodicidad') : t('suscripcion_periodicidad_anual')}
              </div>
              {!esAppNativa && cotizacion && (
                <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 16 }}>
                  ≈ {montoCOPFormateado(cotizacion.montoCOP)} COP
                </div>
              )}
              {(esAppNativa || !cotizacion) && <div style={{ marginBottom: 16 }} />}

              {trialActive && !isPremium && (
                <div style={{ fontSize: 12, marginBottom: 12, padding: '6px 10px', background: 'rgba(255,255,255,0.15)', borderRadius: 10, display: 'inline-block' }}>
                  ⏳ {t('suscripcion_trial_activa')} {diasRestantes} {diasRestantes === 1 ? t('dia_singular') : t('dias_plural')}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {beneficios.map((b, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
                    <span>✅</span><span>{b}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Botón de pago */}
            <button
              onClick={handlePagar}
              disabled={loadingPago || (esAppNativa && (!authReady || !paqueteActualRC))}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: 16,
                border: 'none',
                background: loadingPago
                  ? 'var(--color-muted)'
                  : 'linear-gradient(135deg,var(--stat-income-1),var(--stat-income-2))',
                color: 'white',
                fontWeight: 700,
                fontSize: 15,
                cursor: loadingPago ? 'not-allowed' : 'pointer',
                boxShadow: 'var(--shadow-btn)',
                transition: 'var(--transition)',
              }}
            >
              {loadingPago ? t('suscripcion_procesando') : t('suscripcion_pagar_btn')}
            </button>

            {/* Nota de conversión — solo aplica al flujo web con Wompi */}
            {!esAppNativa && (
              <div style={{ marginTop: 12, fontSize: 11, color: 'var(--color-muted)', textAlign: 'center' }}>
                {t('suscripcion_nota_conversion')}
              </div>
            )}
          </>
        )}

        {/* Botón de gestionar suscripción — solo si ya está suscrito */}
        {isPremium && (
          <button
            onClick={handleGestionarSuscripcion}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: 16,
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface)',
              color: 'var(--color-text)',
              fontWeight: 700,
              fontSize: 14,
              cursor: 'pointer',
              marginTop: 4,
            }}
          >
            {esAppNativa
              ? (t('suscripcion_gestionar_playstore') || 'Gestionar suscripción en Google Play')
              : (t('suscripcion_gestionar_soporte') || 'Contactar soporte para gestionar mi suscripción')}
          </button>
        )}

        {/* Mensaje de error */}
        {errorMsg && (
          <div style={{
            marginTop: 16, padding: '12px 14px', borderRadius: 12,
            background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)',
            color: 'var(--color-text)', fontSize: 13,
          }}>
            ⚠️ {errorMsg}
          </div>
        )}

        {/* Resultado del pago */}
        {estado && (
          <div style={{
            marginTop: 16, padding: '14px 16px', borderRadius: 14,
            background: 'var(--color-surface)', border: '1px solid var(--color-border)',
            fontSize: 14, textAlign: 'center', fontWeight: 600,
          }}>
            {estado === 'APPROVED' && `✅ ${t('suscripcion_pago_aprobado')}`}
            {estado === 'PENDING' && `⏳ ${t('suscripcion_pago_pendiente')}`}
            {estado === 'DECLINED' && `❌ ${t('suscripcion_pago_rechazado')}`}
            {estado === 'ERROR' && `⚠️ ${t('suscripcion_pago_error')}`}
          </div>
        )}

        <div style={{ marginTop: 20, fontSize: 11, color: 'var(--color-muted)', textAlign: 'center' }}>
          {t('suscripcion_nota_seguridad')}
        </div>
      </main>
    </div>
  )
}